import { useEffect, useRef } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import useUserSetting, { useMyUserSettingUpdate } from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { UserSettingName } from '@shared/utils'

import { COLOR_SCHEME_KEY, DEFAULT_COLOR_SCHEME } from './useColorScheme'

const SEND_ON_ENTER_KEY = 'send-on-enter'
const HIDE_PRESENCE_KEY = 'hide-presence'

function readLocal(key) {
  try {
    const raw = window.localStorage.getItem(key)
    return raw === null ? undefined : JSON.parse(raw)
  } catch {
    return undefined
  }
}

/**
 * One-time migration of the legacy localStorage preferences (send on enter,
 * color scheme, hide presence) to the backend user settings.
 *
 * A local value is only pushed up when it differs from the default, so a fresh
 * browser/profile (which has no explicit local value) never overwrites a
 * preference already saved on the backend from another device.
 */
export default function useMigrateUserSettings() {
  const me = useMe()
  const updateUserSetting = useMyUserSettingUpdate(me)

  const sendOnEnter = useUserSetting(me, UserSettingName.SendOnEnter)
  const colorScheme = useUserSetting(me, UserSettingName.ColorScheme)
  const hidePresence = useUserSetting(me, UserSettingName.HidePresence)

  const [, setColorScheme] = useLocalStorage(COLOR_SCHEME_KEY, DEFAULT_COLOR_SCHEME)

  const didRun = useRef(false)

  useEffect(() => {
    if (didRun.current || !me) return
    didRun.current = true

    // Booleans: push a non-default local value up, then drop the local key so
    // the backend becomes the only source of truth.
    migrateBoolean(SEND_ON_ENTER_KEY, sendOnEnter, true)
    migrateBoolean(HIDE_PRESENCE_KEY, hidePresence, false)

    // Color scheme: keep localStorage as the bootstrap mirror for ThemeProvider.
    if (colorScheme?.id) {
      const localMode = readLocal(COLOR_SCHEME_KEY)
      if (localMode && localMode !== DEFAULT_COLOR_SCHEME && localMode !== colorScheme.value) {
        // Legacy local preference not yet on the backend -> migrate it up.
        updateUserSetting.mutateAsync({ settingId: colorScheme.id, value: localMode })
      } else if (colorScheme.value !== localMode) {
        // Backend is the source of truth -> mirror it down so the theme matches.
        setColorScheme(colorScheme.value)
      }
    }

    function migrateBoolean(key, setting, defaultValue) {
      // Keep the local value until the backend setting exists, so we never drop
      // a preference we have nowhere to store yet.
      if (!setting?.id) return

      const localValue = readLocal(key)
      if (localValue !== undefined && localValue !== defaultValue && localValue !== setting.value) {
        updateUserSetting.mutateAsync({ settingId: setting.id, value: String(localValue) })
      }
      window.localStorage.removeItem(key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])
}
